import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Images } from "lucide-react";
import { Navigation, Footer } from "@/pages/LandingPage";
import { useLang } from "@/lib/i18n";
import { ProjectLightbox } from "@/components/ProjectLightbox";

const categories = [
  { id: "all", label: { en: "All", es: "Todos" }, path: "/gallery" },
  { id: "videos", label: { en: "Videos", es: "Videos" }, path: "/gallery/videos" },
  { id: "bathroom", label: { en: "Bathroom Remodeling", es: "Remodelación de Baños" }, path: "/gallery/bathroom" },
  { id: "kitchen", label: { en: "Kitchen Remodeling", es: "Remodelación de Cocinas" }, path: "/gallery/kitchen" },
  { id: "flooring", label: { en: "Flooring & Tile", es: "Pisos y Azulejo" }, path: "/gallery/flooring" },
  { id: "painting", label: { en: "Painting", es: "Pintura" }, path: "/gallery/painting" },
  { id: "handyman", label: { en: "Handyman", es: "Reparaciones" }, path: "/gallery/handyman" }
];

const heroContent = {
  all: {
    en: { eyebrow: "Our Work", title: "Project\nGallery", subtitle: "Browse our projects — open any one to see every photo and video from start to finish." },
    es: { eyebrow: "Nuestro Trabajo", title: "Galería de\nProyectos", subtitle: "Vea nuestros proyectos: abra cualquiera para ver todas sus fotos y videos de principio a fin." }
  },
  videos: {
    en: { eyebrow: "Watch Our Work", title: "Video\nGallery", subtitle: "See our craftsmanship in action through these project walkthroughs." },
    es: { eyebrow: "Vea Nuestro Trabajo", title: "Galería de\nVideos", subtitle: "Vea nuestro trabajo en acción con estos recorridos de proyectos." }
  },
  bathroom: {
    en: { eyebrow: "Bathroom Projects", title: "Bathroom\nGallery", subtitle: "From demolition to finishing touches — our complete bathroom remodels." },
    es: { eyebrow: "Proyectos de Baños", title: "Galería de\nBaños", subtitle: "De la demolición a los últimos detalles: nuestras remodelaciones completas de baños." }
  },
  kitchen: {
    en: { eyebrow: "Kitchen Projects", title: "Kitchen\nGallery", subtitle: "Modern kitchen renovations with custom cabinetry and quality finishes." },
    es: { eyebrow: "Proyectos de Cocinas", title: "Galería de\nCocinas", subtitle: "Renovaciones modernas de cocinas con gabinetes a la medida y acabados de calidad." }
  },
  flooring: {
    en: { eyebrow: "Flooring Projects", title: "Flooring\nGallery", subtitle: "Hardwood, tile, vinyl, and more — premium flooring installations." },
    es: { eyebrow: "Proyectos de Pisos", title: "Galería de\nPisos", subtitle: "Madera, azulejo, vinil y más: instalaciones de pisos de primera." }
  },
  painting: {
    en: { eyebrow: "Painting Projects", title: "Painting\nGallery", subtitle: "Interior and exterior painting that transforms every space." },
    es: { eyebrow: "Proyectos de Pintura", title: "Galería de\nPintura", subtitle: "Pintura interior y exterior que transforma cada espacio." }
  },
  handyman: {
    en: { eyebrow: "Handyman Projects", title: "Handyman\nGallery", subtitle: "From small fixes to bigger upgrades — quality handyman services." },
    es: { eyebrow: "Reparaciones Generales", title: "Galería de\nReparaciones", subtitle: "Desde arreglos pequeños hasta mejoras grandes: reparaciones de calidad." }
  }
};

const projects = [
  {
    id: "tub-wall-panels",
    category: "bathroom",
    cover: 41,
    items: [34, 35, 36, 37, 38, 39, 40, 41],
    title: { en: "Tub & Wall Panel Bathroom Remodel", es: "Remodelación de Baño con Tina y Paneles" },
    description: {
      en: "Old tub surround removed, water-damaged floor repaired and new drain plumbing installed. Finished with a new tub, seamless wall panels with built-in shelves, handheld shower and new vinyl plank flooring.",
      es: "Quitamos el forro viejo de la tina, reparamos el piso dañado por agua e instalamos plomería nueva de drenaje. Terminado con tina nueva, paneles de pared con repisas integradas, regadera de mano y piso nuevo de vinil."
    }
  },
  {
    id: "glass-tile-shower",
    category: "bathroom",
    cover: 9,
    items: [3, 4, 5, 6, 7, 8, 9, "v2", "v1"],
    title: { en: "Full Shower Remodel — Glass Subway Tile", es: "Remodelación Completa de Regadera — Azulejo de Vidrio" },
    description: {
      en: "Complete tear-out down to the studs, plumbing repairs and mold removal, waterproof cement board with RedGard membrane, then glass subway tile with a mosaic accent strip and sliding glass doors.",
      es: "Demolición completa hasta la estructura, reparación de plomería y remoción de moho, tablero de cemento impermeabilizado con RedGard, y azulejo subway de vidrio con franja de mosaico y puertas corredizas de vidrio."
    }
  },
  {
    id: "custom-tile-shower",
    category: "bathroom",
    cover: 1,
    items: [1, 2],
    title: { en: "Custom Tile Shower", es: "Regadera con Azulejo a la Medida" },
    description: {
      en: "Custom tiled shower with glass door and full bathroom tile work.",
      es: "Regadera con azulejo a la medida, puerta de vidrio y trabajo completo de azulejo en el baño."
    }
  },
  {
    id: "shower-tile-vanity",
    category: "bathroom",
    cover: 15,
    items: [16, 15],
    title: { en: "Shower Tile & New Vanity", es: "Azulejo de Regadera y Nuevo Lavabo" },
    description: {
      en: "White subway tile shower with niches and a built-in quartz bench, plus a new white vanity with marble-look quartz top.",
      es: "Regadera con azulejo subway blanco, nichos y banca integrada de cuarzo, además de un nuevo lavabo blanco con cubierta de cuarzo tipo mármol."
    }
  },
  {
    id: "white-kitchen",
    category: "kitchen",
    cover: 10,
    items: [10, 11],
    title: { en: "Modern White Kitchen Remodel", es: "Remodelación de Cocina Blanca Moderna" },
    description: {
      en: "White shaker cabinets, subway tile backsplash, quartz countertops, stainless steel appliances and luxury vinyl plank flooring.",
      es: "Gabinetes blancos estilo shaker, backsplash de azulejo subway, cubiertas de cuarzo, electrodomésticos de acero inoxidable y piso de vinil de lujo."
    }
  },
  {
    id: "cabinet-dishwasher",
    category: "handyman",
    cover: 14,
    items: [12, 13, 14],
    title: { en: "Cabinet, Countertop & Dishwasher Install", es: "Instalación de Gabinetes, Cubierta y Lavaplatos" },
    description: {
      en: "New cabinets and countertop installed, cabinet modified to fit a new dishwasher, then the dishwasher connected to plumbing and electrical.",
      es: "Instalamos gabinetes y cubierta nuevos, adaptamos el gabinete para un lavaplatos nuevo y lo conectamos a la plomería y a la electricidad."
    }
  },
  {
    id: "interior-painting-cabinets",
    category: "painting",
    cover: 29,
    items: [17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, "v3"],
    title: { en: "Full Interior Painting & Cabinet Refinishing", es: "Pintura Interior Completa y Restauración de Gabinetes" },
    description: {
      en: "Whole-home interior painting: careful floor and wall protection, spray-painted walls and ceilings, and kitchen and laundry cabinets refinished in white. Includes a before-and-after video.",
      es: "Pintura interior de toda la casa: protección cuidadosa de pisos y paredes, paredes y techos pintados con pistola, y gabinetes de cocina y lavandería restaurados en blanco. Incluye video de antes y después."
    }
  },
  {
    id: "home-vinyl-plank",
    category: "flooring",
    cover: 32,
    items: [31, 32],
    title: { en: "Luxury Vinyl Plank — Home Installation", es: "Piso de Vinil de Lujo — Casa" },
    description: {
      en: "Wood-look luxury vinyl plank installed across the living area and hallway, with clean cuts around walls and floor vents.",
      es: "Piso de vinil de lujo tipo madera instalado en la sala y el pasillo, con cortes limpios alrededor de paredes y rejillas."
    }
  },
  {
    id: "commercial-vinyl-plank",
    category: "flooring",
    cover: 33,
    items: [33],
    title: { en: "Commercial Space — Vinyl Plank Flooring", es: "Local Comercial — Piso de Vinil" },
    description: {
      en: "Large commercial room fitted with durable vinyl plank flooring and a clean transition to the existing tile.",
      es: "Amplio local comercial con piso de vinil resistente y una transición limpia al azulejo existente."
    }
  }
];

const categoryLabel = { bathroom: "Baño", kitchen: "Cocina", flooring: "Pisos", painting: "Pintura", handyman: "Reparaciones" };

const galleryItems = [
  {
    id: "v1",
    type: "video",
    category: "videos",
    src: "/videos/project-video.mp4",
    poster: "/videos/project-video-poster.jpg",
    title: "Project Walkthrough",
    description: "Behind the scenes of a recent renovation project."
  },
  {
    id: "v2",
    type: "video",
    category: "bathroom",
    src: "/videos/bathroom-tile-video.mp4",
    poster: "/videos/bathroom-tile-poster.jpg",
    title: "Tile Walkthrough",
    description: "Close-up of finished glass subway tile shower with mosaic accent strip"
  },
  {
    id: 1,
    type: "image",
    category: "bathroom",
    image: "https://customer-assets.emergentagent.com/job_site-snap/artifacts/4unhndch_20240806_172201.jpg",
    title: "Shower Remodel",
    description: "Custom tile shower with glass door"
  },
  {
    id: 2,
    type: "image",
    category: "bathroom",
    image: "https://customer-assets.emergentagent.com/job_site-snap/artifacts/rn145yxo_20240805_152008.jpg",
    title: "Bathroom Renovation",
    description: "Complete bathroom tile work"
  },
  {
    id: 3,
    type: "image",
    category: "bathroom",
    image: "https://customer-assets.emergentagent.com/job_ccf15a11-6d33-4c9c-9ff0-3f0c213b7bbc/artifacts/z2m7cqrp_20260129_145525.jpg",
    title: "Demolition Phase",
    description: "Removing old shower stall and prepping window with R-TEC insulation"
  },
  {
    id: 4,
    type: "image",
    category: "bathroom",
    image: "https://customer-assets.emergentagent.com/job_ccf15a11-6d33-4c9c-9ff0-3f0c213b7bbc/artifacts/f7mx3c6b_20260129_145531.jpg",
    title: "Tear-Out in Progress",
    description: "Old tub and shower stall being removed for full bathroom remodel"
  },
  {
    id: 5,
    type: "image",
    category: "bathroom",
    image: "https://customer-assets.emergentagent.com/job_ccf15a11-6d33-4c9c-9ff0-3f0c213b7bbc/artifacts/jpoa6vbh_20260129_152603.jpg",
    title: "Full Bathroom Demolition",
    description: "Complete tear-down: shower, tub, and floor tiles removed for a fresh start"
  },
  {
    id: 6,
    type: "image",
    category: "bathroom",
    image: "https://customer-assets.emergentagent.com/job_ccf15a11-6d33-4c9c-9ff0-3f0c213b7bbc/artifacts/bldm0y14_20260129_155751.jpg",
    title: "Plumbing & Mold Remediation",
    description: "Walls opened to address plumbing and remove mold before rebuild"
  },
  {
    id: 7,
    type: "image",
    category: "bathroom",
    image: "https://customer-assets.emergentagent.com/job_ccf15a11-6d33-4c9c-9ff0-3f0c213b7bbc/artifacts/wip3272m_20260216_171253.jpg",
    title: "Waterproof Shower Build-Out",
    description: "USG Durock cement board with RedGard waterproofing membrane and new shower pan"
  },
  {
    id: 8,
    type: "image",
    category: "bathroom",
    image: "https://customer-assets.emergentagent.com/job_ccf15a11-6d33-4c9c-9ff0-3f0c213b7bbc/artifacts/lrgwqh16_20260318_123859.jpg",
    title: "Glass Subway Tile Install",
    description: "Premium glass subway tile with mosaic accent strip — final stages of installation"
  },
  {
    id: 9,
    type: "image",
    category: "bathroom",
    image: "https://customer-assets.emergentagent.com/job_ccf15a11-6d33-4c9c-9ff0-3f0c213b7bbc/artifacts/02t1kcip_20260324_110554.jpg",
    title: "Finished Shower Enclosure",
    description: "Completed shower with sliding glass doors, brushed nickel hardware, and luxury vinyl plank flooring"
  },
  {
    id: 15,
    type: "image",
    category: "bathroom",
    image: "https://customer-assets.emergentagent.com/job_ccf15a11-6d33-4c9c-9ff0-3f0c213b7bbc/artifacts/7o3c6dmn_20240319_163047.jpg",
    title: "Shower Tile In Progress",
    description: "White subway tile installation underway with pre-cut niche openings, built-in bench with quartz top, and mosaic floor preparation"
  },
  {
    id: 16,
    type: "image",
    category: "bathroom",
    image: "https://customer-assets.emergentagent.com/job_ccf15a11-6d33-4c9c-9ff0-3f0c213b7bbc/artifacts/4dp5mw09_20240313_111717.jpg",
    title: "New Bathroom Vanity Install",
    description: "Custom white vanity with marble-look quartz countertop and black hardware — ready for sink and faucet installation"
  },
  {
    id: 34,
    type: "image",
    category: "bathroom",
    image: "/images/bathp1.webp",
    title: "Tub & Panel Project — Old Surround Removed",
    description: "Second bathroom project: the old tub and wall surround removed, exposing damaged walls and flooring that had to be addressed first."
  },
  {
    id: 35,
    type: "image",
    category: "bathroom",
    image: "/images/bathp2.webp",
    title: "Test-Fitting the New Tub & Wall Panels",
    description: "Dry-fitting the new tub and wall panel kit to confirm measurements and mark the drain and valve locations before the final install."
  },
  {
    id: 36,
    type: "image",
    category: "bathroom",
    image: "/images/bathp3.webp",
    title: "Opening the Floor — Old Drain Lines",
    description: "Floor opened to reach the old drain and supply lines, revealing the water damage that needed repair under the tub."
  },
  {
    id: 37,
    type: "image",
    category: "bathroom",
    image: "/images/bathp4.webp",
    title: "New Drain Plumbing Installed",
    description: "New ABS drain assembly and PEX supply lines connected, with a new shower valve ready for the tub."
  },
  {
    id: 38,
    type: "image",
    category: "bathroom",
    image: "/images/bathp5.webp",
    title: "New Tub Set in Place",
    description: "New tub installed and leveled over the repaired floor, walls prepped and ready for the shower wall panels."
  },
  {
    id: 39,
    type: "image",
    category: "bathroom",
    image: "/images/bathp6.webp",
    title: "Shower Wall Panels Installed",
    description: "Seamless one-piece style wall panels with built-in shelves installed over the new tub — waterproof, easy to clean, and no grout lines."
  },
  {
    id: 40,
    type: "image",
    category: "bathroom",
    image: "/images/bathp7.webp",
    title: "Finished Tub & Shower — Final Result",
    description: "Completed bathroom with new tub, wall panels, handheld shower and chrome fixtures, plus new wood-look vinyl plank flooring."
  },
  {
    id: 41,
    type: "image",
    category: "bathroom",
    image: "/images/bathp8.webp",
    title: "Bathroom Complete — Tub, Panels & Vanity",
    description: "The finished bathroom with the new tub and wall panels, handheld shower, vanity back in place and new vinyl plank floor — ready for the family to use."
  },
  {
    id: 10,
    type: "image",
    category: "kitchen",
    image: "https://customer-assets.emergentagent.com/job_ccf15a11-6d33-4c9c-9ff0-3f0c213b7bbc/artifacts/kehpr6ol_20250821_174834.jpg",
    title: "Modern White Kitchen Remodel",
    description: "Full kitchen renovation with white shaker cabinets, subway tile backsplash, quartz countertops, and luxury vinyl plank flooring"
  },
  {
    id: 11,
    type: "image",
    category: "kitchen",
    image: "https://customer-assets.emergentagent.com/job_ccf15a11-6d33-4c9c-9ff0-3f0c213b7bbc/artifacts/umi0hjtx_20250821_174843.jpg",
    title: "Compact Kitchen Renovation",
    description: "Maximizing small kitchen space with white cabinets, classic subway tile, and stainless appliances"
  },
  {
    id: 12,
    type: "image",
    category: "handyman",
    image: "https://customer-assets.emergentagent.com/job_ccf15a11-6d33-4c9c-9ff0-3f0c213b7bbc/artifacts/o3fgofnh_20240301_155641.jpg",
    title: "Cabinet & Countertop Install",
    description: "Custom cabinet installation with quartz countertop — ready for appliance integration"
  },
  {
    id: 13,
    type: "image",
    category: "handyman",
    image: "https://customer-assets.emergentagent.com/job_ccf15a11-6d33-4c9c-9ff0-3f0c213b7bbc/artifacts/z5phqood_20240309_114500.jpg",
    title: "Dishwasher Opening Cut-Out",
    description: "Precise cabinet modification to create a proper opening for new dishwasher installation with electrical & plumbing prep"
  },
  {
    id: 14,
    type: "image",
    category: "handyman",
    image: "https://customer-assets.emergentagent.com/job_ccf15a11-6d33-4c9c-9ff0-3f0c213b7bbc/artifacts/au1bmwyd_20240309_122530.jpg",
    title: "New Dishwasher Installation",
    description: "Stainless steel dishwasher professionally installed with new faucet, hooked up to plumbing and electrical"
  },
  {
    id: 17,
    type: "image",
    category: "painting",
    image: "https://customer-assets.emergentagent.com/job_ccf15a11-6d33-4c9c-9ff0-3f0c213b7bbc/artifacts/hhcjg818_20260622_153926.webp",
    title: "Living Room — Before Painting",
    description: "Spacious living room with large windows before full interior repaint. Walls and ceiling in original condition, ready for prep work."
  },
  {
    id: 18,
    type: "image",
    category: "painting",
    image: "https://customer-assets.emergentagent.com/job_ccf15a11-6d33-4c9c-9ff0-3f0c213b7bbc/artifacts/jp5vysoo_20260622_153940.webp",
    title: "Dining Area — Before Painting",
    description: "Dining area with vintage cabinets and dated wall color, ready for a full interior refresh."
  },
  {
    id: 19,
    type: "image",
    category: "painting",
    image: "https://customer-assets.emergentagent.com/job_ccf15a11-6d33-4c9c-9ff0-3f0c213b7bbc/artifacts/lj2wt3gd_20260622_153959.webp",
    title: "Entryway — Before Painting",
    description: "Front entryway with worn walls and dated color scheme before complete interior repaint."
  },
  {
    id: 20,
    type: "image",
    category: "painting",
    image: "/images/pfab8lkd_rotated.webp",
    title: "Prep Work — Floor & Wall Protection",
    description: "Careful masking and floor protection with contractor paper before spraying walls and ceilings — professional prep for a clean, damage-free finish."
  },
  {
    id: 21,
    type: "image",
    category: "painting",
    image: "https://customer-assets.emergentagent.com/job_ccf15a11-6d33-4c9c-9ff0-3f0c213b7bbc/artifacts/5m82ggtj_20260623_160636.webp",
    title: "Painting In Progress — Living Areas",
    description: "Full interior repaint underway — fresh white walls and ceilings replacing outdated color scheme throughout the home."
  },
  {
    id: 22,
    type: "image",
    category: "painting",
    image: "/images/sq07knh8_rotated.webp",
    title: "Cabinet Refinishing — Prep Phase",
    description: "Interior cabinet frames masked with plastic and paint tape, drawers removed and prepped for professional spray finish."
  },
  {
    id: 23,
    type: "image",
    category: "painting",
    image: "/images/45xp785o_rotated.webp",
    title: "Full Kitchen Protection Before Spray",
    description: "Every appliance, counter, and surface carefully wrapped and taped — professional-grade masking to guarantee an overspray-free result."
  },
  {
    id: 24,
    type: "image",
    category: "painting",
    image: "/images/62qkn633_rotated.webp",
    title: "Cabinet Doors — Outdoor Spray Booth",
    description: "Cabinet doors and drawer fronts sprayed and hung outside in a custom protected drying setup for an even, factory-quality finish."
  },
  {
    id: 25,
    type: "image",
    category: "painting",
    image: "/images/695qjuj9_rotated.webp",
    title: "Cabinet Drawers — Drying After Spray",
    description: "All cabinet drawers laid out on sawhorses to cure evenly after being sprayed with high-quality paint — smooth, drip-free results every time."
  },
  {
    id: 26,
    type: "image",
    category: "painting",
    image: "/images/zqu7urv3_rotated.webp",
    title: "Entryway — After Painting",
    description: "Complete transformation: bright white walls, fresh ceiling, new luxury vinyl plank flooring, and clean trim throughout the entry."
  },
  {
    id: 27,
    type: "image",
    category: "painting",
    image: "/images/1noa9wlk_rotated.webp",
    title: "Laundry Room — After Painting",
    description: "Refreshed laundry area with crisp white cabinets, updated wall color, and modern light fixture — a clean, functional space."
  },
  {
    id: 28,
    type: "image",
    category: "painting",
    image: "/images/gx78ubwv_rotated.webp",
    title: "Kitchen — After Cabinet Refinishing",
    description: "The finished kitchen with newly-sprayed white cabinets, mosaic tile backsplash, and updated appliances — dramatic before-and-after transformation."
  },
  {
    id: 29,
    type: "image",
    category: "painting",
    image: "/images/w5mdwpfy_rotated.webp",
    title: "Full Kitchen — After Painting",
    description: "Wide view of the completed kitchen showing all refinished cabinets, refrigerator, and updated aesthetics from floor to ceiling."
  },
  {
    id: 30,
    type: "image",
    category: "painting",
    image: "/images/1s2ai977_rotated.webp",
    title: "Kitchen with Natural Light — After",
    description: "Bright kitchen filled with natural light through large windows, showcasing the professional cabinet refinishing and fresh interior paint."
  },
  {
    id: 31,
    type: "image",
    category: "flooring",
    image: "/images/floor1_rotated.webp",
    title: "Luxury Vinyl Plank — Installation in Progress",
    description: "Wood-look luxury vinyl plank going down across an open living area, with plank boxes and tools still on site as the installation moves room to room."
  },
  {
    id: 32,
    type: "image",
    category: "flooring",
    image: "/images/floor2_rotated.webp",
    title: "Living Area — New Vinyl Plank Floor",
    description: "Seamless plank layout running through the living area and into the hallway, cut clean around walls and floor vents."
  },
  {
    id: 33,
    type: "image",
    category: "flooring",
    image: "/images/floor3_rotated.webp",
    title: "Commercial Space — Vinyl Plank Installation",
    description: "Large commercial room fitted with durable vinyl plank flooring, finished with a clean transition to the existing tile floor."
  },
  {
    id: "v3",
    type: "video",
    category: "painting",
    src: "/videos/painting-before-after.mp4",
    poster: "/videos/painting-before-after-poster.jpg",
    title: "Before & After — Full Transformation",
    description: "Complete walkthrough showing the dramatic before-and-after of a full interior repaint and cabinet refinishing project."
  }
];

export default function GalleryPage() {
  const { category: urlCategory } = useParams();
  const navigate = useNavigate();
  const validCategoryIds = categories.map(c => c.id);
  const activeCategory = (urlCategory && validCategoryIds.includes(urlCategory)) ? urlCategory : "all";
  const [openProject, setOpenProject] = useState(null);
  const { t, lang } = useLang();
  const mediaById = Object.fromEntries(galleryItems.map((m) => [m.id, m]));
  const mediaOf = (p) => p.items.map((id) => mediaById[id]);
  const hasVideo = (p) => mediaOf(p).some((m) => m.type === "video");

  const filteredProjects = activeCategory === "all"
    ? projects
    : activeCategory === "videos"
      ? projects.filter(hasVideo)
      : projects.filter((p) => p.category === activeCategory);

  const openGallery = (p) => {
    const media = mediaOf(p);
    const start = activeCategory === "videos" ? Math.max(0, media.findIndex((m) => m.type === "video")) : 0;
    setOpenProject({ project: p, start });
  };

  const hero = (heroContent[activeCategory] || heroContent.all)[lang];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-blue-900 relative overflow-hidden" data-testid="gallery-hero">
        <div className="absolute inset-0 grid-bg opacity-10"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.p 
            key={`eyebrow-${activeCategory}`}
            className="font-mono text-xs tracking-[0.2em] uppercase text-emerald-400 mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {hero.eyebrow}
          </motion.p>
          <motion.h1 
            key={`title-${activeCategory}`}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight uppercase leading-none mb-6 font-['Archivo'] whitespace-pre-line"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            {hero.title}
          </motion.h1>
          <motion.p 
            key={`subtitle-${activeCategory}`}
            className="text-blue-200 text-base md:text-lg leading-relaxed max-w-2xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {hero.subtitle}
          </motion.p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-white border-b border-slate-200 sticky top-[73px] z-40">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-3 overflow-x-auto pb-2">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => navigate(cat.path)}
                className={`px-5 py-2 text-sm font-medium whitespace-nowrap transition-colors rounded-sm ${
                  activeCategory === cat.id
                    ? 'bg-emerald-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`filter-${cat.id}`}
              >
                {t(cat.label)}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16" data-testid="gallery-grid">
        <div className="max-w-7xl mx-auto px-6">
          {filteredProjects.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-slate-500">{t("No projects in this category yet. Coming soon!", "Todavía no hay proyectos en esta categoría. ¡Muy pronto!")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProjects.map((p, i) => {
                const media = mediaOf(p);
                const cover = mediaById[p.cover];
                const photos = media.filter((m) => m.type === "image").length;
                const videos = media.length - photos;
                return (
                  <motion.button
                    type="button"
                    key={p.id}
                    className="group text-left"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                    onClick={() => openGallery(p)}
                    data-testid={`project-card-${p.id}`}
                  >
                    <div className="relative aspect-[4/3]">
                      {media.length > 1 && <div className="absolute inset-0 translate-x-2 -translate-y-2 bg-slate-300" aria-hidden="true"></div>}
                      <div className="absolute inset-0 overflow-hidden bg-slate-200">
                        <img
                          src={cover.type === "video" ? cover.poster : cover.image}
                          alt={t(p.title)}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-blue-900/0 group-hover:bg-blue-900/40 transition-colors flex items-center justify-center">
                          <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity font-medium text-sm uppercase tracking-wider">
                            {t("View Project", "Ver Proyecto")}
                          </span>
                        </div>
                        <div className="absolute bottom-3 left-3 flex gap-2">
                          <span className="bg-black/70 text-white text-xs font-medium px-2.5 py-1 flex items-center gap-1.5" data-testid={`project-photo-count-${p.id}`}>
                            <Images className="w-3.5 h-3.5" /> {photos} {photos === 1 ? t("photo", "foto") : t("photos", "fotos")}
                          </span>
                          {videos > 0 && (
                            <span className="bg-emerald-600 text-white text-xs font-medium px-2.5 py-1 flex items-center gap-1.5">
                              <Play className="w-3 h-3 fill-white" /> {videos} video{videos > 1 ? "s" : ""}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="mt-4">
                      <span className="font-mono text-xs tracking-wider uppercase text-emerald-600">
                        {t(p.category, categoryLabel[p.category])}
                      </span>
                      <h3 className="font-bold text-blue-900 font-['Archivo'] mt-1">{t(p.title)}</h3>
                      <p className="text-sm text-slate-500 line-clamp-2">{t(p.description)}</p>
                    </div>
                  </motion.button>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {openProject && (
        <ProjectLightbox
          project={openProject.project}
          media={mediaOf(openProject.project)}
          startIndex={openProject.start}
          onClose={() => setOpenProject(null)}
        />
      )}

      <Footer />
    </div>
  );
}
